import { err, ok, Result } from "neverthrow"
import { exportDateOfService, exportMemberAge, exportMemberBioGenderStr, exportMemberName } from "../../persistence/exportInfo"
import { BaseRunContext } from "./RunContext"
import { helperFunctions } from "../functions/HelperFunctions"
import {
	ASTNode,
	ExpressionNode,
	NodeType,
	IfStatementNode,
	ValidationReturnStatementNode,
	ForLoopNode,
	ProgramNode,
	AssignmentStatementNode,
	PrintStatementNode,
	ValueReturnStatementNode,
	ActivationReturnStatementNode,
	ConstantNode,
	BinaryExpressionNode,
	NumericLiteralNode,
	StringLiteralNode,
	BooleanLiteralNode,
	IdentifierNode,
	ArrayLiteralNode,
	EmptyReturnStatementNode
} from "../scriptcompiler/AstNode"
import { CalcType } from "../scriptcompiler/CompilerTypes"
import { Constants } from "../scriptcompiler/Token"
import { CalcRuntimeEnv } from "./RuntimeEnv"
import {
	CalcRuntimeError,
	CalcRuntimeExprScalarValue,
	CalcRuntimeExprValue,
	CalcRuntimePrintEntry,
	CalcRuntimeReturnValueEnvelope,
	CompiledCalcEvaluateOk,
	ValueCalcRuntimeReturnValueItem,
} from "./RuntimeTypes"
import { evaluatorErrorWithNode } from "./EvaluatorError"

// the Evaluator runs a compiled script and returns its return value;
// it doesn't know about its own CalcType and doesn't try to police the output,
// instead tagging it with the expected CalcType to be checked on the
// receiving side.
export class CompiledCalcScriptEvaluator {
	private isUsed = false
	private environment = new CalcRuntimeEnv()
	private rootNode: ASTNode
	private context: BaseRunContext
	private continueExecuted: boolean = false
	private returned?: CalcRuntimeReturnValueEnvelope
	private returnNode?: ASTNode
	private printed: CalcRuntimePrintEntry[] = []

	constructor(rootNode: ASTNode, context: BaseRunContext) {
		this.rootNode = rootNode
		this.context = context
	}

	public evaluate(): Result<CompiledCalcEvaluateOk, CalcRuntimeError> {
		if(this.isUsed) {
			return err({
				area: 'CompiledCalcScriptEvaluator',
				message: 'Attempted to reuse instance',
			})
		}
		this.isUsed = true
		const rEvalNode = this.evaluateNode(this.rootNode)
		if(rEvalNode.isErr()) {
			return err(rEvalNode.error)
		}
		if(!this.returned) {
			// we already know since the calc has been run through the compiler
			// that there is at least one RETURN statement somewhere in the
			// script, and that all of the RETURN statements are of the same
			// calc type (value, validation, activation)—
			// so landing here is either a bug in the Evaluator, or more
			// likely, if the Evaluator in the course of this particular
			// evaluation has not yet encountered any RETURN statement
			// statement—  for example if all RETURN statements have existed
			// within conditional blocks that were not evaluated—  then the
			// Evaluator assumes that it is a value calculation with an empty
			// RETURN (no value change).
			// if in actuality the required RETURN statement was simply left
			// off of an activation or validation calculation, this will be
			// caught as an invalid response and thrown as a runtime error by
			// the calculation engine.
			this.returned = {
				calcType: CalcType.Value,
				returnValue: [],
			}
			this.returnNode = {
				type: NodeType.EmptyReturnStatement,
				line: -1,
				column: 0,
			}
		}
		if(!this.returnNode) {
			return err({
				area: 'CompiledCalcScriptEvaluator',
				message: 'Expected return node but null',
			})
		}
		return ok({
			...this.returned,
			returnNode: this.returnNode,
			printed: this.printed,
		})
	}

	private evaluateNode(node: ASTNode): Result<void, CalcRuntimeError> {
		switch (node.type) {
			case NodeType.Program:
				return this.evaluateProgram(node)
			case NodeType.AssignmentStatement:
				return this.evaluateAssignmentStatement(node)
			case NodeType.ContinueStatement:
				return this.evaluateContinueStatement()
			case NodeType.IfStatement:
				return this.evaluateIfStatement(node)
			case NodeType.PrintStatement:
				return this.evaluatePrintStatement(node)
			case NodeType.EmptyReturnStatement:
				return this.evaluateEmptyReturnStatement(node)
			case NodeType.ValueReturnStatement:
				return this.evaluateValueReturnStatement(node)
			case NodeType.ValidationReturnStatement:
				return this.evaluateValidationReturnStatement(node)
			case NodeType.ActivationReturnStatement:
				return this.evaluateActivationReturnStatement(node)
			case NodeType.ForLoop:
				return this.evaluateForLoop(node)
			default:
				return this.evaluateExpressionNode(node).map(() => undefined)
		}
	}

	private evaluateExpressionNode(node: ExpressionNode): Result<CalcRuntimeExprValue, CalcRuntimeError> {
		switch (node.type) {
			case NodeType.BinaryExpression:
				return this.evaluateBinaryExpression(node)
			case NodeType.NumericLiteral:
				return ok(this.evaluateNumericLiteral(node))
			case NodeType.StringLiteral:
				return ok(this.evaluateStringLiteral(node))
			case NodeType.BooleanLiteral:
				return ok(this.evaluateBooleanLiteral(node))
			case NodeType.NullLiteral:
				return ok(this.evaluateNullLiteral())
			case NodeType.Identifier:
				return this.evaluateIdentifier(node)
			case NodeType.Array:
				return this.evaluateArrayLiteral(node)
			case NodeType.Constant:
				return ok(this.evaluateConstant(node))
			default: {
				const descriptor = helperFunctions.find(h => h.nodeType === node.type)
				if (descriptor) {
					return descriptor.evaluate(node, n => this.evaluateExpressionNode(n), this.context)
				}
				return err(evaluatorErrorWithNode(`Unknown function node type: ${node.type}`, node))
			}
		}
	}

	private evaluateProgram(node: ProgramNode): Result<void, CalcRuntimeError> {
		for (const statement of node.body) {
			const result = this.evaluateNode(statement)
			if (result.isErr()) return result
			// already returned, stop evaluating
			if (this.returned !== undefined) break
		}
		return ok()
	}

	private evaluateAssignmentStatement(node: AssignmentStatementNode): Result<void, CalcRuntimeError> {
		const result = this.evaluateExpressionNode(node.expression)
		if (result.isErr()) return err(result.error)
		this.environment.setValue(node.identifier, result.value)
		return ok()
	}

	private evaluateScalarExprNode(node: ExpressionNode): Result<CalcRuntimeExprScalarValue, CalcRuntimeError> {
		const result = this.evaluateExpressionNode(node)
		if (result.isErr()) return err(result.error)
		if (Array.isArray(result.value)) {
			return err(evaluatorErrorWithNode('Expected scalar expression but got array', node))
		}
		return ok(result.value)
	}

	private evaluateForLoop(node: ForLoopNode): Result<void, CalcRuntimeError> {
		if (node.collection.type === NodeType.Array) {
			for (const element of node.collection.elements) {
				const valResult = this.evaluateScalarExprNode(element)
				if (valResult.isErr()) return err(valResult.error)
				this.environment.setValue(node.loopVariable, valResult.value)
				const bodyResult = this.evaluateLoopBody(node.loopBody)
				if (bodyResult.isErr()) return bodyResult
				// already returned, stop evaluating
				if (this.returned !== undefined) return ok()
			}
		} else {
			const elementsResult = this.evaluateExpressionNode(node.collection)
			if (elementsResult.isErr()) return err(elementsResult.error)
			if (!Array.isArray(elementsResult.value)) {
				return err(evaluatorErrorWithNode('FOR loop collection must evaluate to an array', node))
			}
			for (const val of elementsResult.value) {
				this.environment.setValue(node.loopVariable, val)
				const bodyResult = this.evaluateLoopBody(node.loopBody)
				if (bodyResult.isErr()) return bodyResult
				// already returned, stop evaluating
				if (this.returned !== undefined) return ok()
			}
		}
		return ok()
	}

	private evaluateLoopBody(loopBody: ASTNode[]): Result<void, CalcRuntimeError> {
		for (const statement of loopBody) {
			this.continueExecuted = false
			const result = this.evaluateNode(statement)
			if (result.isErr()) return result
			if (this.continueExecuted) break
			// already returned, stop evaluating
			if (this.returned !== undefined) return ok()
		}
		return ok()
	}

	private evaluateConstant(node: ConstantNode): CalcRuntimeExprValue {
		const exp = this.context.getExport()
		switch (node.name) {
			case Constants.MEMBER_AGE:        return exportMemberAge(exp) ?? null
			case Constants.MEMBER_NAME:       return exportMemberName(exp) ?? null
			case Constants.MEMBER_GENDER:     return exportMemberBioGenderStr(exp) ?? null
			case Constants.CLINICIAN_NAME:    return exp.Form.Encounter.ClinicianNameDegree ?? null
			case Constants.DATE_OF_SERVICE:   return exportDateOfService(exp) ?? 'Unknown Date'
			case Constants.IS_VIDEO_REQUIRED: return exp.Form.Member.IsVideoRequired ?? false
			default:                          return null
		}
	}

	private evaluateContinueStatement(): Result<void, CalcRuntimeError> {
		this.continueExecuted = true
		return ok()
	}

	private evaluateIfStatement(node: IfStatementNode): Result<void, CalcRuntimeError> {
		const conditionResult = this.evaluateExpressionNode(node.condition)
		if (conditionResult.isErr()) return err(conditionResult.error)

		if (conditionResult.value) {
			return this.evaluateStatements(node.consequent)
		} else if (node.alternate) {
			if (Array.isArray(node.alternate)) {
				return this.evaluateStatements(node.alternate)
			} else {
				return this.evaluateIfStatement(node.alternate)
			}
		}
		return ok()
	}

	private evaluatePrintStatement(node: PrintStatementNode): Result<void, CalcRuntimeError> {
		const result = this.evaluateExpressionNode(node.argument)
		if (result.isErr()) return err(result.error)
		this.printed.push({
			message: `${result.value}`,
			line: node.line,
			column: node.column,
		})
		return ok()
	}

	private evaluateEmptyReturnStatement(node: EmptyReturnStatementNode): Result<void, CalcRuntimeError> {
		if(this.returned !== undefined) {
			return err(evaluatorErrorWithNode('Already returned', node))
		}
		this.returned = {
			calcType: CalcType.Value,
			returnValue: [],
		}
		this.returnNode = node
		return ok()
	}

	private evaluateValueReturnStatement(node: ValueReturnStatementNode): Result<void, CalcRuntimeError> {
		if(this.returned !== undefined) {
			return err(evaluatorErrorWithNode('Already returned', node))
		}
		const rawResult = this.evaluateExpressionNode(node.value)
		if (rawResult.isErr()) return err(rawResult.error)

		const rawValue = rawResult.value
		if (Array.isArray(rawValue)) {
			return err(evaluatorErrorWithNode('Return value must evaluate to a scalar', node))
		}

		let returnValue: ValueCalcRuntimeReturnValueItem[]
		returnValue = [{ value: rawValue, subconceptCid: undefined }]

		if (node.subconceptId) {
			const subconceptResult = this.evaluateExpressionNode(node.subconceptId)
			if (subconceptResult.isErr()) return err(subconceptResult.error)

			const subconceptIdValue = subconceptResult.value
			if (typeof subconceptIdValue === 'string') {
				returnValue[0].subconceptCid = subconceptIdValue
			} else if (Array.isArray(subconceptIdValue)) {
				returnValue = subconceptIdValue.map(cid => ({ value: rawValue, subconceptCid: String(cid) }))
			} else {
				return err(evaluatorErrorWithNode('Subconcept ID must evaluate to a string or array', node))
			}
		}

		this.returned = {
			calcType: CalcType.Value,
			returnValue,
		}
		this.returnNode = node
		return ok()
	}

	private evaluateValidationReturnStatement(node: ValidationReturnStatementNode): Result<void, CalcRuntimeError> {
		if(this.returned !== undefined) {
			return err(evaluatorErrorWithNode('Already returned', node))
		}
		this.returned = {
			calcType: CalcType.Validation,
			returnValue: {
				determinate: node.validity,
				message: node.message,
			}
		}
		this.returnNode = node
		return ok()
	}

	private evaluateActivationReturnStatement(node: ActivationReturnStatementNode): Result<void, CalcRuntimeError> {
		if(this.returned !== undefined) {
			return err(evaluatorErrorWithNode('Already returned', node))
		}
		this.returned = {
			calcType: CalcType.Activation,
			returnValue: node.activation,
		}
		this.returnNode = node
		return ok()
	}

	private evaluateStatements(statements: ASTNode[]): Result<void, CalcRuntimeError> {
		for (const statement of statements) {
			const result = this.evaluateNode(statement)
			if (result.isErr()) return result
			// already returned, stop evaluating
			if (this.returned !== undefined) return ok()
		}
		return ok()
	}

	private evaluateBinaryExpression(node: BinaryExpressionNode): Result<CalcRuntimeExprValue, CalcRuntimeError> {
		const leftResult = this.evaluateExpressionNode(node.left)
		if (leftResult.isErr()) return leftResult
		const rightResult = this.evaluateExpressionNode(node.right)
		if (rightResult.isErr()) return rightResult

		const left = leftResult.value
		const right = rightResult.value

		switch (node.operator) {
			case '+':
				if (typeof left === 'string' || typeof right === 'string') {
					return ok(String(left ?? '') + String(right ?? ''))
				}
				return ok(Number(left) + Number(right))
			case '-':  return ok(Number(left) - Number(right))
			case '*':  return ok(Number(left) * Number(right))
			case '/':  return ok(Number(left) / Number(right))
			case '==': return ok(left == right)
			case '!=': return ok(left != right)
			case '<':  return ok(Number(left) < Number(right))
			case '>':  return ok(Number(left) > Number(right))
			case '<=': return ok(Number(left) <= Number(right))
			case '>=': return ok(Number(left) >= Number(right))
			case 'AND': return ok(left && right)
			case 'OR':  return ok(left || right)
			default:
				return err(evaluatorErrorWithNode(`Unknown operator '${node.operator}'`, node))
		}
	}

	private evaluateNumericLiteral(node: NumericLiteralNode): number {
		return Number(node.value)
	}

	private evaluateBooleanLiteral(node: BooleanLiteralNode): boolean {
		return node.value
	}

	private evaluateNullLiteral(): null {
		return null
	}

	private evaluateStringLiteral(node: StringLiteralNode): string {
		if (node.value.startsWith('"') && node.value.endsWith('"')) {
			return node.value.slice(1, -1)
		}
		return node.value
	}

	private evaluateArrayLiteral(node: ArrayLiteralNode): Result<CalcRuntimeExprValue, CalcRuntimeError> {
		const values: CalcRuntimeExprScalarValue[] = []
		for (const element of node.elements) {
			const result = this.evaluateScalarExprNode(element)
			if (result.isErr()) return err(result.error)
			values.push(result.value)
		}
		return ok(values)
	}

	private evaluateIdentifier(node: IdentifierNode): Result<CalcRuntimeExprValue, CalcRuntimeError> {
		const r = this.environment.getValue(node.name)
		if(r.isErr()) {
			return err(evaluatorErrorWithNode(r.error, node))
		}
		return ok(r.value)
	}

}